// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { useDebouncedValue } from '@mantine/hooks';
import type { Bundle, BundleEntry, Communication, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { PENDING_INTAKE_TAG, SELF_PAY_TAG_SYSTEM, TEST_PATIENT_TAG_CODE } from '@polarissleep/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { COMMUNICATION_CREATED_EVENT, PATIENT_CREATED_EVENT, PATIENT_UPDATED_EVENT } from '../utils/patient-updates';

const PAGE_SIZE = 30;
const SEARCH_RESULT_LIMIT = 30;
const SEARCH_DEBOUNCE_MS = 300;
const TEST_PATIENT_TAG = `${SELF_PAY_TAG_SYSTEM}|${TEST_PATIENT_TAG_CODE}`;

export interface UsePatientListResult {
  patients: Patient[];
  lastContactDates: Record<string, string>;
  isLoading: boolean;
  isFetchingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
}

interface UsePatientListOptions {
  search: string;
  pendingOnly?: boolean;
}

function entriesToPatients(bundle: Bundle<Patient> | undefined): Patient[] {
  return (bundle?.entry ?? []).map((e: BundleEntry<Patient>) => e.resource).filter((r): r is Patient => Boolean(r?.id));
}

function dedupeById(patients: Patient[]): Patient[] {
  const seen = new Set<string>();
  const out: Patient[] = [];
  for (const p of patients) {
    if (p.id && !seen.has(p.id)) {
      seen.add(p.id);
      out.push(p);
    }
  }
  return out;
}

const AUTOMATED_NOTIFICATION_TAG = 'notification';

function isManualCommunication(communication: Communication): boolean {
  return !(communication.meta?.tag ?? []).some((tag) => tag.code === AUTOMATED_NOTIFICATION_TAG);
}

function communicationDate(communication: Communication): string | undefined {
  return communication.sent ?? communication.meta?.lastUpdated;
}

function getPatientId(reference: string | undefined): string | undefined {
  const match = reference?.match(/^Patient\/(.+)$/);
  return match?.[1];
}

export function usePatientList({ search, pendingOnly = false }: UsePatientListOptions): UsePatientListResult {
  const medplum = useMedplum();
  const [debouncedSearch] = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);

  const [patients, setPatients] = useState<Patient[]>([]);
  const [lastContactDates, setLastContactDates] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState<number | undefined>(undefined);

  const requestIdRef = useRef(0);

  const loadLastContactDates = useCallback(
    async (nextPatients: Patient[], requestId: number, replace: boolean): Promise<void> => {
      const entries = await Promise.all(
        nextPatients
          .filter((patient) => patient.id)
          .map(async (patient) => {
            const patientRef = `Patient/${patient.id}`;
            const [bySubject, byRecipient] = await Promise.all([
              medplum.searchResources('Communication', { subject: patientRef, _sort: '-sent', _count: '100' }),
              medplum.searchResources('Communication', { recipient: patientRef, _sort: '-sent', _count: '100' }),
            ]);
            const latest = [...(bySubject as Communication[]), ...(byRecipient as Communication[])]
              .filter(isManualCommunication)
              .sort(
                (a, b) => new Date(communicationDate(b) ?? 0).getTime() - new Date(communicationDate(a) ?? 0).getTime()
              )[0];
            const date = latest ? communicationDate(latest) : undefined;
            return date ? [patient.id as string, date] : undefined;
          })
      );
      if (requestId !== requestIdRef.current) {
        return;
      }
      const nextDates = Object.fromEntries(entries.filter((entry): entry is [string, string] => Boolean(entry)));
      setLastContactDates((previous) => (replace ? nextDates : { ...previous, ...nextDates }));
    },
    [medplum]
  );

  const fetchEmptySearch = useCallback(
    async (nextOffset: number, append: boolean) => {
      const requestId = ++requestIdRef.current;
      if (append) {
        setIsFetchingMore(true);
      } else {
        setIsLoading(true);
      }
      setError(null);
      try {
        const bundle = await medplum.search('Patient', {
          _count: String(PAGE_SIZE),
          _offset: String(nextOffset),
          _sort: '-_lastUpdated',
          _total: 'accurate',
          '_tag:not': TEST_PATIENT_TAG,
          ...(pendingOnly ? { _tag: `${PENDING_INTAKE_TAG.system}|${PENDING_INTAKE_TAG.code}` } : {}),
        });
        if (requestId !== requestIdRef.current) {
          return;
        }
        const next = entriesToPatients(bundle);
        setPatients((prev) => (append ? dedupeById([...prev, ...next]) : next));
        loadLastContactDates(next, requestId, !append).catch(console.error);
        setTotal(bundle.total);
        setOffset(nextOffset + next.length);
      } catch (err) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setError(err instanceof Error ? err : new Error('Failed to load patients'));
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsFetchingMore(false);
        }
      }
    },
    [loadLastContactDates, medplum, pendingOnly]
  );

  const fetchSearchResults = useCallback(
    async (term: string) => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      setIsFetchingMore(false);
      setError(null);
      try {
        const commonParams = {
          _count: String(SEARCH_RESULT_LIMIT),
          _sort: '-_lastUpdated',
          '_tag:not': TEST_PATIENT_TAG,
          ...(pendingOnly ? { _tag: `${PENDING_INTAKE_TAG.system}|${PENDING_INTAKE_TAG.code}` } : {}),
        };
        const [byName, byIdentifier, byTelecom] = await Promise.all([
          medplum.search('Patient', { ...commonParams, 'name:contains': term }),
          medplum.search('Patient', { ...commonParams, 'identifier:contains': term }),
          medplum.search('Patient', { ...commonParams, 'telecom:contains': term }),
        ]);
        if (requestId !== requestIdRef.current) {
          return;
        }
        const merged = dedupeById([
          ...entriesToPatients(byName),
          ...entriesToPatients(byIdentifier),
          ...entriesToPatients(byTelecom),
        ]).slice(0, SEARCH_RESULT_LIMIT);
        setPatients(merged);
        loadLastContactDates(merged, requestId, true).catch(console.error);
        setTotal(merged.length);
        setOffset(merged.length);
      } catch (err) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setError(err instanceof Error ? err : new Error('Failed to search patients'));
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [loadLastContactDates, medplum, pendingOnly]
  );

  useEffect(() => {
    setOffset(0);
    setTotal(undefined);
    if (debouncedSearch) {
      fetchSearchResults(debouncedSearch).catch(console.error);
    } else {
      fetchEmptySearch(0, false).catch(console.error);
    }
  }, [debouncedSearch, fetchEmptySearch, fetchSearchResults]);

  useEffect(() => {
    const refreshPatients = (): void => {
      if (debouncedSearch) {
        fetchSearchResults(debouncedSearch).catch(console.error);
      } else {
        fetchEmptySearch(0, false).catch(console.error);
      }
    };

    const handlePatientCreated = (event: Event): void => {
      const createdPatient = (event as CustomEvent<Patient>).detail;
      if (createdPatient?.id) {
        refreshPatients();
      }
    };

    const handlePatientUpdated = (event: Event): void => {
      const updatedPatient = (event as CustomEvent<Patient>).detail;
      if (!updatedPatient?.id) {
        return;
      }

      setPatients((prev) => prev.map((patient) => (patient.id === updatedPatient.id ? updatedPatient : patient)));
    };

    const handleCommunicationCreated = (event: Event): void => {
      const communication = (event as CustomEvent<Communication>).detail;
      const patientId = getPatientId(communication?.subject?.reference ?? communication?.recipient?.[0]?.reference);
      const date = communicationDate(communication);
      if (patientId && date && isManualCommunication(communication)) {
        setLastContactDates((prev) => {
          const current = prev[patientId];
          return !current || new Date(date).getTime() > new Date(current).getTime()
            ? { ...prev, [patientId]: date }
            : prev;
        });
      }
    };

    window.addEventListener(PATIENT_CREATED_EVENT, handlePatientCreated);
    window.addEventListener(PATIENT_UPDATED_EVENT, handlePatientUpdated);
    window.addEventListener(COMMUNICATION_CREATED_EVENT, handleCommunicationCreated);
    return () => {
      window.removeEventListener(PATIENT_CREATED_EVENT, handlePatientCreated);
      window.removeEventListener(PATIENT_UPDATED_EVENT, handlePatientUpdated);
      window.removeEventListener(COMMUNICATION_CREATED_EVENT, handleCommunicationCreated);
    };
  }, [debouncedSearch, fetchEmptySearch, fetchSearchResults]);

  const loadMore = useCallback(() => {
    if (debouncedSearch) {
      return;
    }
    if (isFetchingMore || isLoading) {
      return;
    }
    fetchEmptySearch(offset, true).catch(console.error);
  }, [debouncedSearch, fetchEmptySearch, isFetchingMore, isLoading, offset]);

  const hasMore = !debouncedSearch && total !== undefined && patients.length < total;

  return { patients, lastContactDates, isLoading, isFetchingMore, error, hasMore, loadMore };
}
